"use client";

import { useState, useEffect, useRef, useTransition } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { Search, ChevronDown, X, Loader2, Check } from "lucide-react";

const TYPE_OPTS = [
  { v: "all",    l: "Tất cả loại" },
  { v: "exam",   l: "📝 Đề thi" },
  { v: "path",   l: "🗺️ Lộ trình" },
  { v: "system", l: "⭐ Hệ thống" },
] as const;

const RATING_OPTS = [
  { v: "0", l: "Tất cả sao" },
  { v: "5", l: "★★★★★ 5 sao" },
  { v: "4", l: "★★★★ 4 sao" },
  { v: "3", l: "★★★ 3 sao" },
  { v: "2", l: "★★ 2 sao" },
  { v: "1", l: "★ 1 sao" },
] as const;

const SORT_OPTS = [
  { v: "created_at", l: "Mới nhất" },
  { v: "rating",     l: "⭐ Cao nhất" },
] as const;

type DropdownKey = "type" | "rating" | "sort";

export default function ReviewsToolbar({
  initialType,
  initialRating,
  initialSort,
  initialQ,
}: {
  initialType: string;
  initialRating: number;
  initialSort: string;
  initialQ: string;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();

  // ── Search state với debounce ──
  const [q, setQ] = useState(initialQ);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (q === initialQ) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      pushParams({ q: q.trim() || null });
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // ── Dropdown open state — chỉ 1 cái mở 1 lúc ──
  const [openKey, setOpenKey] = useState<DropdownKey | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Click outside → close
  useEffect(() => {
    if (!openKey) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpenKey(null);
      }
    };
    // ESC → close
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenKey(null);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", keyHandler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", keyHandler);
    };
  }, [openKey]);

  // ── Push params → URL → server re-render ──
  function pushParams(updates: Record<string, string | null>) {
    const params = new URLSearchParams(searchParams.toString());
    for (const [k, v] of Object.entries(updates)) {
      if (v === null || v === "" || v === "all" || v === "0" || (k === "sort" && v === "created_at")) {
        params.delete(k);
      } else {
        params.set(k, v);
      }
    }
    params.delete("page"); // reset pagination khi đổi filter
    const qs = params.toString();
    startTransition(() => {
      router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    });
  }

  function selectOption(key: DropdownKey, val: string) {
    setOpenKey(null);
    if (key === "type")   pushParams({ type: val === "all" ? null : val });
    if (key === "rating") pushParams({ rating: val === "0" ? null : val });
    if (key === "sort")   pushParams({ sort: val === "created_at" ? null : val });
  }

  function clearSearch() {
    setQ("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    pushParams({ q: null });
  }

  const currentType   = TYPE_OPTS.find(o => o.v === initialType)?.l   ?? "Tất cả loại";
  const currentRating = RATING_OPTS.find(o => o.v === String(initialRating))?.l ?? "Tất cả sao";
  const currentSort   = SORT_OPTS.find(o => o.v === initialSort)?.l   ?? "Mới nhất";

  return (
    <div ref={wrapperRef} className="bg-white rounded-2xl border border-[#e2e8f0] p-3 relative">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">

        {/* ═══ TRÁI: Search ═══ */}
        <div className="relative w-full md:w-64 shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748b] pointer-events-none" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Tìm kiếm..."
            enterKeyHint="search"
            className="w-full h-9 rounded-lg border border-[#e2e8f0] bg-white pl-9 pr-9 text-[12px] font-medium outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 transition-all placeholder:text-[#94a3b8]"
          />
          {q ? (
            <button
              type="button"
              onClick={clearSearch}
              aria-label="Xóa tìm kiếm"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-[#f1f5f9] flex items-center justify-center text-[#64748b]"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          ) : isPending ? (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-teal-600 animate-spin" />
          ) : null}
        </div>

        {/* ═══ PHẢI: 3 Dropdown ═══ */}
        <div className="flex gap-2 w-full md:w-auto overflow-x-auto md:overflow-visible scrollbar-thin -mx-1 px-1 md:mx-0 md:px-0">
          <DropdownButton
            label={currentType}
            isOpen={openKey === "type"}
            onToggle={() => setOpenKey(openKey === "type" ? null : "type")}
            options={TYPE_OPTS}
            currentValue={initialType}
            onSelect={(v) => selectOption("type", v)}
            align="left"
          />
          <DropdownButton
            label={currentRating}
            isOpen={openKey === "rating"}
            onToggle={() => setOpenKey(openKey === "rating" ? null : "rating")}
            options={RATING_OPTS}
            currentValue={String(initialRating)}
            onSelect={(v) => selectOption("rating", v)}
            align="left"
          />
          <DropdownButton
            label={currentSort}
            isOpen={openKey === "sort"}
            onToggle={() => setOpenKey(openKey === "sort" ? null : "sort")}
            options={SORT_OPTS}
            currentValue={initialSort}
            onSelect={(v) => selectOption("sort", v)}
            align="right"
          />
        </div>
      </div>
    </div>
  );
}

// ─── Dropdown button component ───
function DropdownButton({
  label,
  isOpen,
  onToggle,
  options,
  currentValue,
  onSelect,
  align,
}: {
  label: string;
  isOpen: boolean;
  onToggle: () => void;
  options: readonly { v: string; l: string }[];
  currentValue: string;
  onSelect: (v: string) => void;
  align: "left" | "right";
}) {
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={`inline-flex items-center justify-between gap-2 h-9 px-3 rounded-lg border bg-white text-[12px] font-semibold text-[#0f172a] transition-all whitespace-nowrap min-w-[120px] ${
          isOpen ? "border-teal-600 ring-2 ring-teal-600/20" : "border-[#e2e8f0] hover:border-teal-500"
        }`}
      >
        <span className="truncate">{label}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-[#64748b] shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      {isOpen && (
        <div
          role="listbox"
          className={`absolute z-30 ${align === "right" ? "right-0" : "left-0"} mt-1.5 min-w-[180px] bg-white rounded-lg border border-[#e2e8f0] shadow-lg overflow-hidden py-1 animate-in fade-in slide-in-from-top-1`}
        >
          {options.map((o) => {
            const active = currentValue === o.v;
            return (
              <button
                key={o.v}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => onSelect(o.v)}
                className={`w-full text-left flex items-center justify-between gap-2 px-3 py-2 text-[12px] font-medium hover:bg-teal-50 transition-colors ${
                  active ? "bg-teal-50 text-teal-700 font-bold" : "text-[#0f172a]"
                }`}
              >
                <span>{o.l}</span>
                {active && <Check className="w-3.5 h-3.5 text-teal-600 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}